import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, Sparkles, ArrowRight, Award, ShieldCheck, Users2, Rocket, QrCode, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PixPaymentDialog from "@/components/PixPaymentDialog";
import PagarmePixDialog from "@/components/PagarmePixDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planIcons = { Starter: Rocket, Growth: TrendingUp, Partner: Award, Founder: Sparkles };

export default function Investors() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [pixPlan, setPixPlan] = useState(null); // manual admin-confirmed pix flow
  const [pagarmePlan, setPagarmePlan] = useState(null); // pagar.me automated pix flow
  const [pagarmeConfigured, setPagarmeConfigured] = useState(false);
  const [payMethodOpen, setPayMethodOpen] = useState(null); // {plan_id, amount}

  useEffect(() => {
    axios.get(`${API}/investments`).then(r => setPlans(r.data));
    if (user) setForm(f => ({ ...f, name: user.name || "", email: user.email || "", phone: user.phone || "" }));
  }, [user]);

  useEffect(() => {
    // Detect Pagar.me availability once — silent failure ok.
    axios.get(`${API}/pagarme/status`).then(r => setPagarmeConfigured(!!r.data.configured)).catch(() => {});
  }, []);

  const openInterest = (p) => {
    setSelected(p);
  };

  // Stripe deposit path was retired in iteration 44; investor deposits now use Pagar.me Pix
  // (auto) with manual admin-confirmed Pix as a fallback.

  const submit = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await axios.post(`${API}/investments/${selected.plan_id}/interest`, form);
      toast.success("Interesse registrado! Nossa equipe entrará em contato.");
      setSelected(null);
      setForm({ ...form, message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-primary via-blue-600 to-blue-800 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        <Badge className="bg-white/20 text-white border-0 mb-3">Seja sócio</Badge>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-3">
          Invista no FUTURO PDV
        </h1>
        <p className="text-white/80 max-w-2xl">
          Faça parte da super-app que une social, freelance e mobilidade.
          Escolha um plano abaixo e demonstre seu interesse — nossa equipe entrará em contato com detalhes personalizados.
        </p>
        <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg">
          <div>
            <div className="text-2xl font-bold">10k+</div>
            <div className="text-xs text-white/70">Usuários potenciais</div>
          </div>
          <div>
            <div className="text-2xl font-bold">3</div>
            <div className="text-xs text-white/70">Verticais em 1 app</div>
          </div>
          <div>
            <div className="text-2xl font-bold">25%</div>
            <div className="text-xs text-white/70">Retorno até</div>
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="font-bold text-sm">Contrato registrado</div>
            <div className="text-xs text-muted-foreground">Termos claros e transparência total</div>
          </div>
        </Card>
        <Card className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="font-bold text-sm">Retorno mensal</div>
            <div className="text-xs text-muted-foreground">Pagamento proporcional ao plano</div>
          </div>
        </Card>
        <Card className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0">
            <Users2 className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <div className="font-bold text-sm">Comunidade de sócios</div>
            <div className="text-xs text-muted-foreground">Acesso a eventos e insights</div>
          </div>
        </Card>
      </div>

      {/* Plans */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-4">Planos disponíveis</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => {
            const Icon = planIcons[p.name] || TrendingUp;
            const monthly = ((p.amount * p.roi_pct / 100) / 12);
            return (
              <Card
                key={p.plan_id}
                data-testid={`plan-${p.plan_id}`}
                className={`p-6 flex flex-col hover-lift relative ${p.featured ? "border-primary/60 shadow-lg" : ""}`}
              >
                {p.featured && (
                  <Badge className="absolute -top-2 right-4 bg-primary">Mais popular</Badge>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${p.featured ? "bg-primary" : "bg-primary/10"}`}>
                  <Icon className={`w-5 h-5 ${p.featured ? "text-white" : "text-primary"}`} />
                </div>
                <h3 className="text-xl font-bold tracking-tight">{p.name}</h3>
                <div className="my-4">
                  <div className="text-3xl font-bold tracking-tight">R$ {p.amount.toLocaleString("pt-BR")}</div>
                  <div className="text-xs text-muted-foreground mt-1">Aporte único</div>
                </div>
                <div className="space-y-2 text-sm mb-6 flex-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Retorno</span>
                    <span className="font-bold text-green-600" data-testid={`plan-roi-${p.plan_id}`}>{p.roi_pct}% a.a.</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Prazo</span>
                    <span className="font-semibold">{p.term_months} meses</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Est. mensal</span>
                    <span className="font-semibold">R$ {monthly.toFixed(2)}</span>
                  </div>
                  {p.description && (
                    <p className="text-xs text-muted-foreground pt-3 border-t border-border">{p.description}</p>
                  )}
                </div>
                <Button
                  data-testid={`interest-btn-${p.plan_id}`}
                  onClick={() => openInterest(p)}
                  className="w-full rounded-full"
                  variant="outline"
                >
                  Demonstrar interesse <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  data-testid={`invest-btn-${p.plan_id}`}
                  onClick={() => setPayMethodOpen({ plan_id: p.plan_id, amount: p.min_amount || p.amount || 0, name: p.name })}
                  className="w-full rounded-full mt-2"
                  variant={p.featured ? "default" : "outline"}
                >
                  Investir agora
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Interest Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demonstrar interesse · {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-muted/50 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-muted-foreground">Aporte</span>
                <span className="font-bold">R$ {selected?.amount.toLocaleString("pt-BR")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Retorno</span>
                <span className="font-bold text-green-600">{selected?.roi_pct}% a.a.</span>
              </div>
            </div>
            <Input data-testid="interest-name" placeholder="Nome completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="interest-email" placeholder="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="interest-phone" placeholder="Telefone/WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Textarea data-testid="interest-message" placeholder="Mensagem (opcional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <Button data-testid="interest-submit" onClick={submit} disabled={loading} className="w-full rounded-full h-11">
              {loading ? "Enviando..." : "Enviar interesse"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Nossa equipe entrará em contato em até 48h úteis
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment method choice */}
      <Dialog open={!!payMethodOpen} onOpenChange={(v) => !v && setPayMethodOpen(null)}>
        <DialogContent className="max-w-sm" data-testid="invest-pay-method-dialog">
          <DialogHeader><DialogTitle>Como quer pagar?</DialogTitle></DialogHeader>
          {payMethodOpen && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Plano: <strong>{payMethodOpen.name}</strong><br />
                Valor mínimo: <strong>R$ {Number(payMethodOpen.amount).toFixed(2)}</strong>
              </div>
              <Button
                data-testid="invest-pay-pix"
                onClick={() => {
                  setPixPlan(payMethodOpen);
                  setPayMethodOpen(null);
                }}
                variant={pagarmeConfigured ? "outline" : "default"}
                className={pagarmeConfigured
                  ? "w-full rounded-full h-11 gap-2"
                  : "w-full rounded-full h-11 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white"}
              >
                <QrCode className="w-4 h-4" /> {pagarmeConfigured ? "Pix manual (aprovação admin)" : "Pagar com Pix"}
              </Button>
              {pagarmeConfigured && (
                <Button
                  data-testid="invest-pay-pagarme"
                  onClick={() => {
                    setPagarmePlan(payMethodOpen);
                    setPayMethodOpen(null);
                  }}
                  className="w-full rounded-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Zap className="w-4 h-4" /> Pix instantâneo (Pagar.me)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PixPaymentDialog
        open={!!pixPlan}
        onOpenChange={(v) => !v && setPixPlan(null)}
        purpose="investor_deposit"
        amount={pixPlan?.amount || 0}
        refId={pixPlan?.plan_id}
        note={pixPlan ? `Plano ${pixPlan.name}` : ""}
      />
      <PagarmePixDialog
        open={!!pagarmePlan}
        onOpenChange={(v) => !v && setPagarmePlan(null)}
        flow="checkout"
        amount={pagarmePlan?.amount || 0}
        purpose="investor_deposit"
        description={pagarmePlan ? `Plano investidor ${pagarmePlan.name || ""}` : "Investidor"}
        referenceId={pagarmePlan?.plan_id}
      />
    </div>
  );
}
